import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist";
import { mediaSrc } from "../api";
import { FileText } from "lucide-react";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

/** First-page preview of a PDF, rendered once to a small canvas (cropped like a photo thumbnail). */
export function PdfThumbnail({ media, width = 170, maxHeight = 185, onClick }) {
  const canvasRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!media || !canvas) return undefined;
    const task = pdfjsLib.getDocument({ url: mediaSrc(media) });
    task.promise.then(async (pdf) => {
      if (cancelled) return;
      try {
        const page = await pdf.getPage(1);
        const raw = page.getViewport({ scale: 1 });
        const scale = width / raw.width;
        const viewport = page.getViewport({ scale });
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        const ctx = canvas.getContext("2d");
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        await page.render({ canvasContext: ctx, viewport }).promise;
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setFailed(true);
      }
    }).catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; try { task.destroy(); } catch (e) { /* ignore */ } };
  }, [media, width]);

  return (
    <button
      type="button"
      data-testid="pdf-thumbnail"
      onClick={onClick}
      className="block w-full text-left rounded-lg overflow-hidden border bg-white hover:opacity-90 transition-opacity"
      style={{ maxWidth: width }}
    >
      {failed ? (
        <div className="flex items-center justify-center bg-muted" style={{ width, height: Math.min(Math.round(width * 1.3), maxHeight) }}>
          <FileText className="h-10 w-10 text-muted-foreground" />
        </div>
      ) : (
        <div className="overflow-hidden" style={{ maxHeight }}>
          <canvas ref={canvasRef} className={`block ${ready ? "" : "bg-muted animate-pulse"}`} style={!ready ? { width, height: maxHeight } : undefined} />
        </div>
      )}
      <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted/60 border-t">
        <FileText className="h-3.5 w-3.5 text-destructive shrink-0" />
        <span className="text-[11px] font-medium truncate">{media?.filename || "PDF"}</span>
      </div>
    </button>
  );
}
